import React, { useState, useEffect, useImperativeHandle } from 'react';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { X } from 'lucide-react';

// Suggestion list component for mentions
const SuggestionList = React.forwardRef((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [props.items]);

  const selectItem = index => {
    if (index >= props.items.length || index < 0) { // Make sure we're not out of bounds
      return;
    }
    props.command({ id: props.items[index].id, label: props.items[index].text });
  };

  // Expose methods to the parent component (Tiptap extension)
  useImperativeHandle(ref, () => ({
    moveUp: () => {
      setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
    },
    moveDown: () => {
      setSelectedIndex((selectedIndex + 1) % props.items.length);
    },
    confirmSelection: () => {
      selectItem(selectedIndex);
    }
  }));

  if (!props.items || props.items.length === 0) {
    return null;
  }

  return (
    <div /* ref={ref} // ref is handled by forwardRef for useImperativeHandle */ className="bg-white shadow-xl border border-gray-200 rounded-lg p-2 space-y-1 max-h-60 overflow-y-auto z-50 min-w-[250px]">
      {props.items.map((item, index) => (
        <button
          type="button"
          className={`w-full text-left text-sm p-2 rounded text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none ${index === selectedIndex ? 'bg-indigo-100 text-indigo-700' : ''}`}
          key={item.id}
          onClick={() => selectItem(index)}
        >
          {item.text}
        </button>
      ))}
    </div>
  );
});

const RichTextEditor = ({ value, onChange, placeholder, questions = [], className = '' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // Disable headings for simplicity for now
        blockquote: false,
        codeBlock: false,
        bulletList: false,
        orderedList: false,
      }),
      Placeholder.configure({ placeholder }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention-pill',
        },
        suggestion: {
          items: ({ query }) => {
            if (!questions) return [];
            return questions
              .filter(item => item.text.toLowerCase().startsWith(query.toLowerCase()))
              .slice(0, 5); // Show top 5 matches
          },
          render: () => {
            let reactRenderer;
            let popup;

            return {
              onStart: props => {
                reactRenderer = new ReactRenderer(SuggestionList, {
                  props,
                  editor: props.editor,
                });
                if (typeof tippy === 'function') {
                    popup = tippy('body', {
                        getReferenceClientRect: props.clientRect,
                        appendTo: () => document.body,
                        content: reactRenderer.element,
                        showOnCreate: true,
                        interactive: true,
                        trigger: 'manual',
                        placement: 'bottom-start',
                        zIndex: 10002,
                    });
                } else {
                    console.error("Tippy.js not loaded for mention suggestions.")
                }
              },
              onUpdate(props) {
                reactRenderer.updateProps(props);
                if (!props.clientRect || !popup || !popup[0]) return;
                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                });
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup?.[0]?.hide();
                  return true;
                }
                if (reactRenderer && reactRenderer.ref) {
                  if (props.event.key === 'ArrowUp') {
                    reactRenderer.ref.moveUp();
                    return true;
                  }
                  if (props.event.key === 'ArrowDown') {
                    reactRenderer.ref.moveDown();
                    return true;
                  }
                  if (props.event.key === 'Enter') {
                    // Prevent default Enter behavior (like creating a new paragraph in Tiptap)
                    props.event.preventDefault(); 
                    reactRenderer.ref.confirmSelection();
                    return true;
                  }
                }
                return false; // Let Tiptap handle other keys
              },
              onExit() {
                popup?.[0]?.destroy();
                reactRenderer?.destroy();
              },
            };
          },
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .insertContentAt(range, [
                {
                  type: 'mention',
                  attrs: { id: props.id, label: props.label },
                },
                { type: 'text', text: ' ' },
              ])
              .run();
            window.getSelection()?.collapseToEnd();
          },
        },
        selectable: false,
        atom: true,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
        attributes: {
            class: `prose prose-sm sm:prose focus:outline-none max-w-none text-sm p-2.5 border border-gray-200 bg-gray-50 focus:bg-white transition-colors rounded-md ${className}`,
        },
    },
  });

  return (
    <div className="relative rich-text-editor-wrapper">
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;

// Add some basic styling for mentions (you'd put this in your global CSS or a styled component)
// This is a placeholder; you'll want to refine this.
const styles = `
.mention-pill {
  background-color: #e0e7ff; /* bg-indigo-100 */
  color: #4338ca; /* text-indigo-700 */
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 0.875rem;
  white-space: nowrap;
  cursor: default;
  user-select: none;
}

.tippy-box {
  background-color: transparent !important; /* Let SuggestionList handle background */
  box-shadow: none !important; /* Let SuggestionList handle shadow */
  padding: 0 !important;
}
`;

if (typeof window !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
} 